import { v4 } from "uuid"
import { ACTION_FREQUENCY_TYPE } from "../common/linqset-model"

export const MAX_QUEUE_FOR_COLOR = 60   // maximum queue value for color coding

export class Item {
    endTick:number = 0          // the tick when the item was completed
    noQueueTime:number = 0      // time it would take if no queue: the work on it
    waitTime:number = 0         // time it spent at steps where items wait, with nobody working on them
    numReceived:number = 0      // howmany times have received it
    
    constructor(public _key:string, public startingNodeKey:string, public startTick:number) {}
}

/**
 * Attached to node in the temporal model. Node is a collapsed representation of actions with their role and system information included.
 * This class manages the processing and queuing of items.
 */
export class TemporalNode {
    proportionLeft:number = 0           // holds proportion of a single item to receive
    totalPercentage:number = 0        // total percentage of sending

    numReceivers:number = 0
    // the inputs that send items to this one: each connection from a step, so a step sending through two pieces of
    // information is two inputs, as the insights count them
    inputs:{from:TemporalNode, channel:object}[] = []
    setsOwnVolume = false               // how many it does is set on it, so items are put in rather than taken from its inputs
    // a step where items wait for someone or something (see WORK_TYPE): any number wait at once, for its duration, and
    // that time is waiting, not work. Items there aren't a queue
    waits = false

    // where inputs join (other than a sum), items wait here until the join lets them through: a minimum runs each time
    // its least input gets ahead of the runs so far, each run taking everything waiting from the other inputs with it
    // (a batch, such as a monthly step that takes the month's orders); a maximum goes with the busiest input and an
    // average with the mean. Counts rather than items are matched, as items split down different routes come back as
    // different items
    onhold:Item[] = []
    private receivedFrom = new Map<object, number>()     // items received from each input (by its channel)
    private released = 0                                       // items let through the join
    unprocessed:Item[] = []          // items waiting to be processed

    processors:any[] = []    // items currently being processed
    sending:Item[] = []
    finished:Item[] = []
    itemsSent:number = 0

    currentTick:number = 0

    // how the step has been running, for the simulation's results
    entered = 0                                 // items that have reached the step's queue
    completed = 0                               // items the step has finished
    maxQueue = 0                                // the most that have waited at once
    dayMarks:{tick:number, queue:number}[] = [] // the queue at the start of the last two simulated days, to see if it's growing

    next:{node: TemporalNode, percentage:number, proportion:number, edge:any, itemsSent:number}[] = []

    // duration, ticktime and capacity are all in seconds. duration is how long it takes to process one item, tick time is how long per tick and capacity it how much capacity per tick, if 0 then no capacity restriction
    constructor(public _key:string, public name:string, public durationSecondsPerItem:number, public numProcessors:number, public type:ACTION_FREQUENCY_TYPE, public processingTimePerTick:number) {
    }

    // the items waiting out their time at a step where items wait: never a queue
    get waiting() {
        return this.waits ? this.processors.length : 0
    }

    get queue() {
        return this.onhold.length + this.unprocessed.length + (this.waits ? 0 : this.processors.length)
    }

    addNext(node:TemporalNode, percentage:number = 100, edge:any = undefined) {
        if (percentage > 0) {
            const channel = {node, percentage, proportion:0, edge, itemsSent:0}
            this.next.push(channel)
            this.totalPercentage += percentage
            node.addReceiver(this, channel)
        }
    }

    addReceiver(from?:TemporalNode, channel?:object) {
        this.numReceivers++
        if (from && channel) this.inputs.push({from, channel})
    }

    // how many items each input has sent, where they join
    inputCounts() {
        return this.inputs.map(i => ({key: i.from._key, name: i.from.name, count: this.receivedFrom.get(i.channel) || 0}))
    }

    reset() {
        this.onhold = []
        this.unprocessed = []
        this.processors = []
        this.sending = []
        this.currentTick = 0
        this.receivedFrom.clear()
        this.released = 0
    }

    // items arriving from another step (from, along one of its connections, the channel), or from outside the graph when there's no step
    public receiveItems(tick:number, items:Item[], from?:TemporalNode, channel?:object) {
        if (from && this.setsOwnVolume) items = []
        if (items.length > 0) {
            if (this.type === ACTION_FREQUENCY_TYPE.SUM || this.inputs.length<=1 || !from) {
                this.unprocessed = this.unprocessed.concat(items)
                this.entered += items.length
            }else {
                this.join(items, channel || from)
            }
        }

        // the step works once a tick, when the first of its inputs arrives; items arriving later in the tick wait for the next
        if (tick <= this.currentTick) return

        if (this.durationSecondsPerItem === 0) {
            this.currentTick = tick;
            this.sending = [...this.unprocessed]
            this.completed += this.unprocessed.length
            this.unprocessed = []
            this.sendItems()
        }else {
            // send all previously processed items
            this.currentTick = tick;
            this.sendItems()

            this.processItems()
        }
        this.maxQueue = Math.max(this.maxQueue, this.queue)

    }

    private join(items:Item[], channel:object) {
        this.onhold.push(...items)
        this.receivedFrom.set(channel, (this.receivedFrom.get(channel) || 0) + items.length)

        const counts = [...this.receivedFrom.values()]
        const total = counts.reduce((sum, c) => sum + c, 0)

        if (this.type === ACTION_FREQUENCY_TYPE.MINIMUM) {
            // until every input has sent something, nothing runs; then a run for each time the least input gets ahead
            const target = counts.length < this.inputs.length ? 0 : Math.min(...counts)
            const release = Math.max(0, target - this.released)
            if (release > 0) this.runBatch(release, items)
            return
        }

        const target = this.type === ACTION_FREQUENCY_TYPE.MAXIMUM ? Math.max(...counts) : Math.floor(total/this.inputs.length)
        const release = Math.max(0, target - this.released)
        this.unprocessed.push(...this.onhold.splice(0, release))
        this.released += release
        this.entered += release

        // what's left waits for its counterparts on the other inputs, and the rest was merged into the items let through.
        // The oldest keep waiting, so the time they wait is counted when they go on
        const waiting = Math.max(0, total - this.inputs.length*this.released)
        if (this.onhold.length > waiting) this.onhold.splice(waiting)
    }

    // a minimum's runs: each takes everything waiting with it, so what goes on carries the average wait of what it
    // took. A run whose items already went with an earlier run (its counterparts arrived first) is made from the item
    // that set it off
    private runBatch(release:number, arrived:Item[]) {
        const consumed = this.onhold
        const mean = (value:(i:Item) => number) => consumed.length ? consumed.reduce((sum, i) => sum + value(i), 0)/consumed.length : 0
        const startTick = mean(i => i.startTick)
        const noQueueTime = mean(i => i.noQueueTime)
        const waitTime = mean(i => i.waitTime)
        const runs = consumed.slice(0, release)
        const last = arrived[arrived.length-1] || consumed[consumed.length-1]
        while (runs.length < release) runs.push(new Item(v4(), last.startingNodeKey, last.startTick))
        runs.forEach(r => {
            r.startTick = startTick
            r.noQueueTime = noQueueTime
            r.waitTime = waitTime
        })
        this.onhold = []
        this.unprocessed.push(...runs)
        this.released += release
        this.entered += release
    }

    private processItems() {
        // move items from queue to processing if there are available processors
        this.unprocessed = this.unprocessed.filter(item => {
            // processing queue can expand to number of processors, if num processor is 0, then it can expand infinitely
            if (this.numProcessors === 0 || this.processors.length < this.numProcessors) {
                // place in processing queue for processing
                this.addTime(item)
                this.processors.push({item, processed: 0})
                return false
            }
            return true
        })
        // update processing time for items in processing queue, and move to sending queue if done
        this.processors = this.processors.filter(p => {
            p.processed += this.processingTimePerTick
            let keep = true         // whether we keep this processor

            while (p.processed >= this.durationSecondsPerItem && keep) {
                this.sending.push(p.item)
                this.completed++
                // take off work done
                p.processed -= this.durationSecondsPerItem
                if (this.unprocessed.length > 0) {
                    // get next item in queue
                    const i = this.unprocessed.shift() // remove from processing queue
                    this.addTime(i)
                    p.item = i
                }else {
                    keep = false
                }
            }
            return keep
        })
    }

    // the time an item spends at the step: work, or waiting where items wait
    private addTime(item:Item) {
        if (this.waits) item.waitTime += this.durationSecondsPerItem
        else item.noQueueTime += this.durationSecondsPerItem
    }

    private sendItems() {
        if (this.next.length === 0) {
            this.sending.forEach(s => {
                s.endTick = this.currentTick
                this.finished.push(s)
            })
        }

        let index = 0

        this.next.forEach(n => {
            if (this.sending.length === 0) {
                n.itemsSent = 0
                n.node.receiveItems(this.currentTick, [], this, n)
            } else {
                const toSend = []
                const totalToSend = n.proportion + (n.percentage*this.sending.length/100)
                const numToSend = Math.round(totalToSend)
                n.itemsSent = numToSend

                n.proportion = totalToSend-numToSend
                if (this.totalPercentage > 100) index = 0           // send from start down each channel

                if (numToSend > 0) {
                    for (let i = 0; i < numToSend; i++) {
                        let item:Item
                        if (index >= this.sending.length) {
                            item = new Item(v4(), this.sending[this.sending.length-1].startingNodeKey, this.sending[this.sending.length-1].startTick)
                            item.noQueueTime = this.sending[this.sending.length-1].noQueueTime
                            item.waitTime = this.sending[this.sending.length-1].waitTime
                        }else if (this.totalPercentage > 100) {
                            item = new Item(this.sending[index]._key, this.sending[index].startingNodeKey, this.sending[index].startTick)
                            item.noQueueTime = this.sending[index].noQueueTime
                            item.waitTime = this.sending[index].waitTime
                        }else {
                            item = this.sending[index]
                        }
                        toSend.push(item)
                        index++
                    }
                }

                n.node.receiveItems(this.currentTick, toSend, this, n)
            }
        })
        this.sending = []
    }
}
